// ----------------------------------------------------------------------------
// Setlists for the active team: Upcoming and History. History is read-only;
// its only action is Delete, which CLEARS the setlist (PUT items: [],
// isSetlistPublished: false) and keeps the service plan and roster.
// Setlists whose team no longer exists ("orphans") can be moved to a team.
// ----------------------------------------------------------------------------

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { plansApi, groupsApi } from '@app/api/organization';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { logger } from '@app/utils/logger';
import { Toast, ConfirmDialog } from '@app/components/Notifications';
import { Banner, Button, EmptyState, ErrorState, LockedChip, PageHeader, Segmented, Select, StatusPill, cx } from '@app/components/ui';
import { isPastDate, longDate, shortDate, time12, plural } from '@app/lib/format';
import type { ServicePlan, Group } from '@app/types';

type Tab = 'upcoming' | 'history';

const songsOf = (p: ServicePlan) => p.items.filter((i) => (i.type || 'Song') === 'Song' || i.songId);

export const Setlists: React.FC = () => {
  const { user } = useAuth();
  const { selectedGroupId, selectedGroup } = useGroup();
  const churchId = user?.churchId;
  const [params, setParams] = useSearchParams();

  const [upcoming, setUpcoming] = useState<ServicePlan[]>([]);
  const [past, setPast] = useState<ServicePlan[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('upcoming');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateTargetGroupId, setMigrateTargetGroupId] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmPlan, setConfirmPlan] = useState<ServicePlan | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => setToast({ type, text });

  const load = useCallback(async () => {
    if (!churchId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [upcomingList, pastList, groupList] = await Promise.all([
        plansApi.listUpcomingAll(churchId, 200),
        plansApi.listPublishedHistory(churchId, 200),
        groupsApi.getByChurch(churchId),
      ]);
      setUpcoming(upcomingList);
      setPast(pastList);
      setGroups(groupList);
      setMigrateTargetGroupId((cur) => cur || groupList.find((g) => g.isActive)?.id || '');
    } catch (err: any) {
      logger.error('Setlists.load failed', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load setlists');
    } finally {
      setIsLoading(false);
    }
  }, [churchId]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeGroupIds = useMemo(() => new Set(groups.filter((g) => g.isActive).map((g) => g.id)), [groups]);
  const isOrphan = useCallback((plan: ServicePlan) => !plan.groupId || !activeGroupIds.has(plan.groupId), [activeGroupIds]);
  const orphanCount = useMemo(() => [...upcoming, ...past].filter(isOrphan).length, [upcoming, past, isOrphan]);

  const inTeam = (p: ServicePlan) => !selectedGroupId || p.groupId === selectedGroupId;
  const list = useMemo(() => {
    const src = tab === 'history' ? past.filter((p) => isPastDate(p.date)) : upcoming.filter((p) => !isPastDate(p.date));
    return src
      .filter(inTeam)
      .sort((a, b) => (tab === 'history' ? +new Date(b.date) - +new Date(a.date) : +new Date(a.date) - +new Date(b.date)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, past, upcoming, selectedGroupId]);

  // Deep links: ?plan=<id>&tab=history|upcoming selects a setlist;
  // ?tab=history on its own opens History (used by Tips).
  useEffect(() => {
    const id = params.get('plan');
    const tabParam = params.get('tab');
    if (!id && !tabParam) return;
    if (id) {
      setTab(tabParam === 'history' || past.some((p) => p.id === id) ? 'history' : 'upcoming');
      setSelectedId(id);
    } else {
      setTab(tabParam === 'history' ? 'history' : 'upcoming');
    }
    const next = new URLSearchParams(params);
    next.delete('plan');
    next.delete('tab');
    setParams(next, { replace: true });
  }, [params, setParams, past]);

  const selected = list.find((p) => p.id === selectedId) ?? list[0] ?? null;

  const handleMigrateOrphans = async () => {
    if (!churchId || !migrateTargetGroupId || orphanCount === 0) return;
    setMigrating(true);
    try {
      const result = await plansApi.migrateOrphanedPlans(churchId, migrateTargetGroupId);
      showToast(
        'success',
        result.migrated > 0 ? `Moved ${result.migrated} orphan setlist${result.migrated === 1 ? '' : 's'} to ${result.targetGroupName}` : 'No orphan setlists to migrate',
      );
      await load();
    } catch (err: any) {
      logger.error('migrateOrphanedPlans failed', err);
      showToast('error', err?.response?.data?.error || err?.message || 'Failed to migrate orphans');
    } finally {
      setMigrating(false);
    }
  };

  const clearSetlist = async (plan: ServicePlan) => {
    if (!churchId) return;
    try {
      // Clear the setlist only — keep the service plan, roster, and metadata.
      // Service plan deletion happens on the Roster page.
      await plansApi.update(churchId, plan.id, { items: [], isSetlistPublished: false });
      // A cleared past plan no longer has a published setlist, so it leaves History.
      setPast((l) => l.filter((p) => p.id !== plan.id));
      setSelectedId(null);
      showToast('success', `Deleted the setlist for "${plan.title || 'Untitled'}"`);
    } catch (err: any) {
      logger.error('clear setlist failed', err);
      showToast('error', err?.response?.data?.error || err?.message || 'Failed to delete setlist');
    }
  };

  const statusFor = (p: ServicePlan) => {
    const n = songsOf(p).length;
    if (isPastDate(p.date)) return { l: 'Played', tone: 'muted' as const };
    if (!n) return { l: 'Not started', tone: 'muted' as const };
    return p.isSetlistPublished ? { l: 'Published', tone: 'ok' as const } : { l: 'Draft', tone: 'amb' as const };
  };

  return (
    <div>
      {toast && <Toast message={toast.text} type={toast.type} onClose={() => setToast(null)} />}
      <PageHeader
        eyebrow={selectedGroup?.name ?? 'All teams'}
        title="Setlists"
        actions={
          <Segmented
            label="Setlists"
            value={tab}
            onChange={(t) => {
              setTab(t);
              setSelectedId(null);
            }}
            options={[
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'history', label: 'History' },
            ]}
          />
        }
      />

      {orphanCount > 0 && groups.length > 0 && (
        <Banner tone="amb" className="flex flex-wrap items-center gap-3 mb-3.5">
          <span className="flex-1 min-w-[220px] text-t1">
            <span className="font-semibold">{plural(orphanCount, 'setlist')} no longer belong to a team.</span> Move them to a team so they show up here.
          </span>
          <div className="w-[200px]">
            <Select
              size="sm"
              aria-label="Move orphan setlists to"
              value={migrateTargetGroupId}
              onChange={setMigrateTargetGroupId}
              options={groups.filter((g) => g.isActive).map((g) => ({ value: g.id, label: g.name }))}
            />
          </div>
          <Button size="sm" className="!bg-amb" loading={migrating} onClick={handleMigrateOrphans}>
            Move setlists
          </Button>
        </Banner>
      )}

      {error ? (
        <ErrorState title="Couldn't load setlists" body={error} onRetry={load} retrying={isLoading} />
      ) : isLoading ? (
        <div className="flex flex-wrap gap-3.5" aria-busy="true">
          <span className="wp-skeleton flex-[1_1_300px] h-72 rounded-[18px]" />
          <span className="wp-skeleton flex-[2_1_480px] h-72 rounded-[24px]" />
        </div>
      ) : list.length === 0 ? (
        <div className="py-[60px] text-center text-t4 text-[14px]">
          {tab === 'history' ? `No past setlists for ${selectedGroup?.name ?? 'this team'}.` : `No upcoming services for ${selectedGroup?.name ?? 'this team'}.`}
        </div>
      ) : (
        <div className="flex flex-wrap gap-3.5 items-start">
          <div className="flex-[1_1_300px] min-w-0 flex flex-col gap-1" role="list" aria-label={tab === 'history' ? 'Past setlists' : 'Upcoming setlists'}>
            {list.map((p) => {
              const st = statusFor(p);
              const on = selected?.id === p.id;
              const n = songsOf(p).length;
              return (
                <button
                  key={p.id}
                  role="listitem"
                  type="button"
                  aria-current={on || undefined}
                  onClick={() => setSelectedId(p.id)}
                  className={cx('flex items-center gap-3 w-full text-left p-3 rounded-[16px] border text-t1', on ? 'bg-card border-line2' : 'bg-transparent border-transparent hover:bg-card')}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14.5px] font-semibold truncate">{p.title}</span>
                    <span className="block text-[12.5px] text-t4 mt-0.5">
                      {shortDate(p.date)} · {n ? plural(n, 'song') : 'No songs yet'}
                    </span>
                  </span>
                  <StatusPill tone={st.tone}>{st.l}</StatusPill>
                </button>
              );
            })}
          </div>

          {selected && (
            <section aria-label="Setlist" className="flex-[2_1_480px] min-w-0 rounded-[24px] border border-line bg-card p-6">
              <div className="flex flex-wrap justify-between gap-3.5 items-start mb-[18px]">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-t4">
                    {longDate(selected.date)} · {time12(selected.date)}
                    {isPastDate(selected.date) && <LockedChip />}
                  </div>
                  <h2 className="text-[24px] font-semibold tracking-[-0.02em] mt-1">{selected.title}</h2>
                  {isOrphan(selected) && <div className="text-[12.5px] text-amb mt-1">Not on a team</div>}
                </div>
                {isPastDate(selected.date) && songsOf(selected).length > 0 && (
                  <Button variant="danger-ghost" size="sm" className="!h-[38px] !px-4 !text-[13.5px]" onClick={() => setConfirmPlan(selected)}>
                    Delete
                  </Button>
                )}
              </div>

              {selected.items.length === 0 ? (
                <EmptyState dashed={false} className="!py-9 bg-fill rounded-[16px]" title="No songs have been added yet" />
              ) : (
                <div role="table" aria-label="Songs">
                  <div role="row" className="grid gap-3 px-1 pb-2.5 wp-eyebrow border-b border-line" style={{ gridTemplateColumns: '36px minmax(0,1fr) minmax(0,150px) 54px' }}>
                    <span role="columnheader">#</span>
                    <span role="columnheader">Song</span>
                    <span role="columnheader">Section</span>
                    <span role="columnheader">Key</span>
                  </div>
                  {selected.items.map((it, i) => (
                    <div
                      key={it.id}
                      role="row"
                      className="grid gap-3 items-center px-1 py-3.5 border-b border-line last:border-0"
                      style={{ gridTemplateColumns: '36px minmax(0,1fr) minmax(0,150px) 54px' }}
                    >
                      <span role="cell" className="font-mono text-[13px] text-t4">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span role="cell" className="min-w-0">
                        <span className="block text-[15px] font-semibold truncate">{it.title}</span>
                        {it.type && it.type !== 'Song' && <span className="block text-[12.5px] text-t4 mt-0.5">{it.type}</span>}
                      </span>
                      <span role="cell" className="text-[13px] text-t3 truncate">
                        {it.section || '–'}
                      </span>
                      <span role="cell">
                        {it.key ? (
                          <span className="inline-flex w-[34px] h-[30px] rounded-[8px] bg-acc-bg text-acc font-mono font-bold text-[13px] items-center justify-center">{it.key}</span>
                        ) : (
                          <span className="text-t5">–</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {!isPastDate(selected.date) && (
                <p className="text-[12.5px] text-t4 mt-4 leading-normal">
                  Upcoming setlists can&apos;t be deleted. Once the service has happened, this setlist moves to History.
                </p>
              )}
            </section>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmPlan}
        onClose={() => setConfirmPlan(null)}
        onConfirm={() => (confirmPlan ? clearSetlist(confirmPlan) : undefined)}
        type="danger"
        title="Delete this setlist?"
        message={
          confirmPlan
            ? `The setlist for ${confirmPlan.title || 'Untitled'} on ${shortDate(confirmPlan.date)} will be removed from History. The service plan and roster stay.`
            : ''
        }
        confirmText="Delete setlist"
      />
    </div>
  );
};
